import { cn, theme } from '@venusprotocol/ui';
import { ChartTooltipContent, type ChartTooltipContentItem, ChartYAxisTick } from 'components';
import { useUID } from 'react-uid';
import {
  Area,
  AreaChart as RCAreaChart,
  ResponsiveContainer,
  Tooltip,
  XAxis,
  YAxis,
} from 'recharts';
import type { DataKey, Margin } from 'recharts/types/util/types';

import { getXAxisTicks } from './getXAxisTicks';
import { getYAxisTicks } from './getYAxisTicks';

export interface AreaChartProps<T extends Record<string, any>> {
  data: T[];
  xAxisDataKey: DataKey<T>;
  yAxisDataKey: DataKey<T>;
  chartColor: string;
  formatXAxisValue: (value: any) => string;
  formatYAxisValue: (value: any) => string;
  yAxisTickCount?: number;
  onDataPointHover?: (value: T) => void;
  onMouseLeave?: () => void;
  formatTooltipItems?: (value: T) => ChartTooltipContentItem[];
  areaChartMargin?: Margin;
  /** Maximum number of labels to display on the X axis. */
  interval?: number;
  displayAxes?: boolean;
  displayToolTip?: boolean;
  className?: string;
}

export const AreaChart = <T extends Record<string, any>>({
  className,
  data,
  chartColor,
  interval,
  xAxisDataKey,
  yAxisDataKey,
  yAxisTickCount = 6,
  formatXAxisValue,
  formatYAxisValue,
  formatTooltipItems,
  onDataPointHover,
  onMouseLeave,
  displayAxes = true,
  displayToolTip = true,
  areaChartMargin,
}: AreaChartProps<T>) => {
  // Generate base ID that won't change between renders but will be incremented
  // automatically every time it is used (so multiple charts can be rendered
  // using unique ids)
  const baseId = useUID();
  const gradientId = `gradient-${baseId}`;
  const xAxisTicks = getXAxisTicks({ data, interval, xAxisDataKey, formatValue: formatXAxisValue });
  const yAxisTicks = getYAxisTicks({
    data,
    tickCount: yAxisTickCount,
    yAxisDataKey,
    formatValue: formatYAxisValue,
  });

  return (
    <div className={cn('w-full h-62', className)}>
      <ResponsiveContainer>
        <RCAreaChart
          margin={{
            top: 20,
            left: -14,
            right: 4,
            ...areaChartMargin,
          }}
          data={data}
          onMouseLeave={onMouseLeave}
          onMouseMove={e => {
            if (e.activePayload) {
              onDataPointHover?.(e.activePayload[0].payload);
            }
          }}
        >
          {/* Gradient used as filler */}
          <defs>
            <linearGradient id={gradientId} x1="0" y1="0" x2="0" y2="1">
              <stop offset="0%" stopColor={chartColor} stopOpacity={0.2} />
              <stop offset="100%" stopColor={chartColor} stopOpacity={0} />
            </linearGradient>
          </defs>

          {displayAxes && (
            <XAxis
              dataKey={xAxisDataKey}
              axisLine={false}
              tickLine={false}
              tickFormatter={formatXAxisValue}
              stroke={theme.colors.grey}
              tickMargin={8}
              tickCount={xAxisTicks?.length ?? data.length}
              ticks={xAxisTicks}
              interval={xAxisTicks ? 0 : undefined}
              padding={{ left: 0, right: 24 }}
              className="text-xs"
            />
          )}

          {displayAxes && (
            <YAxis
              dataKey={yAxisDataKey}
              axisLine={false}
              tickLine={false}
              tickMargin={8}
              tick={({ payload, y }) => (
                <ChartYAxisTick value={formatYAxisValue(payload.value)} y={y} />
              )}
              ticks={yAxisTicks}
              tickCount={yAxisTickCount}
              stroke={theme.colors.grey}
              className="text-xs text-left"
              domain={yAxisTicks ? ['dataMin', 'dataMax'] : ['auto', 'auto']}
            />
          )}

          {displayToolTip && (
            <Tooltip
              isAnimationActive={false}
              cursor={{ strokeDasharray: '4px 4px', stroke: theme.colors.grey }}
              content={({ payload }) =>
                formatTooltipItems && payload?.[0]?.payload ? (
                  <ChartTooltipContent items={formatTooltipItems(payload[0].payload)} />
                ) : null
              }
            />
          )}

          <Area
            isAnimationActive={false}
            dataKey={yAxisDataKey}
            stroke={chartColor}
            strokeWidth={2}
            fillOpacity={1}
            fill={`url(#${gradientId})`}
            activeDot={{ r: 8, strokeWidth: 4 }}
          />
        </RCAreaChart>
      </ResponsiveContainer>
    </div>
  );
};
